/**
 * Header search bar helpers (Smooth Sales, desktop visible bar).
 *
 * The dropdown's visibility is CSS: it shows while the results container
 * holds a [data-search-results] panel AND the form either has focus or is
 * marked [data-open] (behaviour 4). Four behaviours need JS support:
 *
 * 1. Clicks inside the dropdown must not blur the input — in browsers
 *    that don't focus clicked links (Safari) the blur would collapse the
 *    dropdown mid-click and swallow the navigation. Cancelling pointerdown
 *    keeps focus on the input; the click still lands.
 *
 * 2. predictive-search.js is shared with the mobile search modal, and on
 *    reset (Escape, clear, empty input) it morphs the modal's EMPTY-STATE
 *    markup (recently viewed products etc.) into the results container.
 *    The bar has no use for that panel, and its items would linger as
 *    invisible keyboard-selectable results — so whenever the input is
 *    empty the container is emptied too.
 *
 * 3. Signup popups take the cursor away. The Klaviyo "10% off" popup opens
 *    12 seconds after a shopper lands and moves focus onto its close button;
 *    when it closes, Klaviyo moves focus onto its "Subscribe" teaser tab —
 *    never back to where the shopper was. A shopper who was mid-search lost
 *    the dropdown (it needs the bar to have focus) and never got it back:
 *    "mondo" sat in the box with nothing under it, reported as "search
 *    suggestions don't work on the homepage" (proved with ?searchdebug=1:
 *    results painted at 8s, activeEl=klaviyo-close-form at 13s). So when a
 *    Klaviyo popup interrupts a search, focus is handed back to the bar once
 *    the popup closes. The suggestions were never removed, so they reappear
 *    as they were. Klaviyo announces its forms with `klaviyoForms` window
 *    events (type "open" / "close").
 *
 * 4. The dropdown stays open until the SHOPPER closes it. Gated on focus
 *    alone, it vanished whenever anything else took the cursor: the popup
 *    above, but also, with no popup in sight, a shopper who typed "mondo"
 *    and was left looking at an unfocused box with nothing under it
 *    (light border = no focus; reported twice on the homepage). Any script,
 *    browser extension or iframe on the page can take focus, and we cannot
 *    list them all. So typing or focusing the bar marks the form [data-open], and
 *    only the shopper's own actions clear it: a click or tap outside the
 *    bar, Escape, or leaving the page. The :focus-within rule still applies,
 *    so if this module fails to load the bar behaves exactly as before.
 */

/* Every element Klaviyo renders (popup, close button, teaser) carries one. */
const KLAVIYO_SELECTOR = '[class*="kl-private-reset-css"], [class*="klaviyo-"]';
/* Klaviyo focuses its teaser ~100ms after the close event; let it, then
 * take focus back. Its later focus moves inside the window are undone too. */
const RESTORE_DELAY_MS = 250;
const RESTORE_WINDOW_MS = 1500;
/* The popup announces itself (open) before it grabs focus as it animates in;
 * a grab that lands just before the announcement still counts. */
const GRAB_GRACE_MS = 1000;

/** @param {EventTarget | null} target */
const isKlaviyo = (target) => target instanceof Element && target.closest(KLAVIYO_SELECTOR) !== null;

/** Into the search log (snippets/header-search-bar.liquid records it). */
const log = (message) => {
  try {
    /** @type {any} */ (window).__ssSearchDebug?.('[helper] ' + message);
  } catch (e) {
    /* diagnostics must never break search */
  }
};

/** @param {EventTarget | null} el */
const describe = (el) =>
  el instanceof Element
    ? el.tagName.toLowerCase() + (typeof el.className === 'string' && el.className ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.') : '')
    : String(el);

/** @param {HTMLInputElement} input */
function giveFocusBack(input) {
  const until = Date.now() + RESTORE_WINDOW_MS;

  const refocus = () => {
    if (!input.isConnected || input.value.trim() === '') return;
    const active = document.activeElement;
    if (active === input) return;
    // Focus on anything other than the page itself or Klaviyo's own widgets
    // means the shopper chose to go there. Leave them where they are.
    if (active && active !== document.body && !isKlaviyo(active)) return;
    log('popup closed: cursor back in the search box');
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(end, end);
  };

  const onFocusIn = (event) => {
    if (Date.now() <= until && isKlaviyo(event.target)) setTimeout(refocus, 0);
  };
  document.addEventListener('focusin', onFocusIn, true);
  setTimeout(() => document.removeEventListener('focusin', onFocusIn, true), RESTORE_WINDOW_MS);
  setTimeout(refocus, RESTORE_DELAY_MS);
}

/** @param {HTMLInputElement} input */
function restoreAfterPopups(input) {
  /** When a popup opened over a search in progress; null otherwise. */
  let openedAt = null;
  /** When the bar last lost focus straight to a Klaviyo element. */
  let grabbedAt = -Infinity;

  input.addEventListener('focusout', (event) => {
    if (isKlaviyo(/** @type {FocusEvent} */ (event).relatedTarget)) grabbedAt = Date.now();
  });

  window.addEventListener('klaviyoForms', (event) => {
    const type = /** @type {CustomEvent} */ (event).detail?.type;
    if (type === 'open') {
      // Only a search in progress is worth restoring: there is a query, and
      // the shopper was still in the bar when the popup arrived.
      const now = Date.now();
      const searching =
        input.value.trim() !== '' && (document.activeElement === input || now - grabbedAt < GRAB_GRACE_MS);
      openedAt = searching ? now : null;
    } else if (type === 'close' && openedAt !== null) {
      // ...and only if the popup really took focus from the bar. A shopper
      // who clicked away before it grabbed had already left the search.
      if (grabbedAt >= openedAt - GRAB_GRACE_MS) giveFocusBack(input);
      openedAt = null;
    }
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement} input
 */
function keepOpenUntilDismissed(form, input) {
  const open = () => form.setAttribute('data-open', '');
  /** @param {string} why */
  const close = (why) => {
    if (form.hasAttribute('data-open')) log('dropdown closed: ' + why);
    form.removeAttribute('data-open');
  };

  form.addEventListener('focusin', open);
  input.addEventListener('input', open);
  // The bar is typeable before this module runs, so the shopper may already
  // be in it. Their focusin has been and gone; catch up.
  if (form.matches(':focus-within')) open();
  log('ready' + (form.hasAttribute('data-open') ? ' (shopper already in the bar)' : ''));

  // Only the shopper closes it: a click or tap anywhere outside the bar,
  // Escape (which also clears the search), or leaving the page.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!(event.target instanceof Node) || !form.contains(event.target)) close('click on ' + describe(event.target));
    },
    true
  );
  form.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close('Escape');
  });
  window.addEventListener('pagehide', () => close('leaving the page'));
}

function init(bar) {
  const input = bar.querySelector('.header-search-bar__input');
  const results = bar.querySelector('.header-search-bar__results');
  const form = bar.querySelector('.header-search-bar__form');
  if (!input || !results) return;

  results.addEventListener('pointerdown', (event) => event.preventDefault());

  if (form) keepOpenUntilDismissed(form, input);

  const prune = () => {
    if (input.value.trim() === '' && results.childNodes.length > 0) {
      results.replaceChildren();
    }
  };
  new MutationObserver(prune).observe(results, { childList: true, subtree: false });
  prune();

  restoreAfterPopups(input);
}

function initAll() {
  for (const bar of document.querySelectorAll('predictive-search-component.header-search-bar')) {
    init(bar);
  }
}

// The <script> tag sits AFTER the bar (snippets/header-search-bar.liquid), so
// the bar is already parsed when this runs: wire it up NOW. Waiting for
// DOMContentLoaded, as this once did, meant waiting for every deferred module
// on the page, which on the homepage is seconds after the bar is typeable.
// The fallback only matters if the tag is ever moved above the bar again.
if (document.querySelector('predictive-search-component.header-search-bar')) {
  initAll();
} else {
  document.addEventListener('DOMContentLoaded', initAll, { once: true });
}
